import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ChatBubble as ChatIcon,
  AccessTime as ClockIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import { assistantV2Service } from "../../services/assistantV2Service";

const ConversationSidebar = ({
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewChat,
  onDeleteConversation,
  onDeleteAll,
  onRenameConversation,
  loading,
}) => {
  const theme = useTheme();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showRenameDialog, setShowRenameDialog] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);

  const handleMenuOpen = (event, id) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveMenuId(id);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
    setActiveMenuId(null);
  };

  const handleDelete = async (conversationId) => {
    try {
      await assistantV2Service.deleteConversation(conversationId);
      onDeleteConversation(conversationId);
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
    setShowDeleteConfirm(null);
  };

  const handleDeleteAll = async () => {
    try {
      await assistantV2Service.deleteAllHistory();
      onDeleteAll();
    } catch (error) {
      console.error("Error deleting all conversations:", error);
    }
    setShowDeleteConfirm(null);
  };

  const handleRename = async (conversationId) => {
    try {
      await assistantV2Service.renameConversation(conversationId, newTitle);
      onRenameConversation(conversationId, newTitle);
      setShowRenameDialog(null);
      setNewTitle("");
    } catch (error) {
      console.error("Error renaming conversation:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    if (date.toDateString() === today.toDateString()) {
      return "Aujourd'hui";
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return "Hier";
    }
    if (date >= weekAgo) {
      return "Cette semaine";
    }
    return "Plus ancien";
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupConversations = (convs) => {
    const groups = {
      "Aujourd'hui": [],
      Hier: [],
      "Cette semaine": [],
      "Plus ancien": [],
    };

    convs.forEach((conv) => {
      const group = formatDate(conv.created_at);
      if (groups[group]) {
        groups[group].push(conv);
      }
    });

    return groups;
  };

  const filteredConversations = conversations.filter((conv) =>
    (conv.title || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const groupedConversations = groupConversations(filteredConversations);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      {/* Header with New Chat button */}
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            boxShadow: theme.shadows[2],
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
            "&:hover": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          Nouveau chat
        </Button>
      </Box>

      {/* Search bar */}
      <Box sx={{ px: 2, pb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Rechercher..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                bgcolor: "grey.50",
              },
            },
          }}
        />
      </Box>

      {/* Delete all button */}
      {conversations.length > 0 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Button
            fullWidth
            color="error"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={() => setShowDeleteConfirm("all")}
            sx={{
              textTransform: "none",
              justifyContent: "flex-start",
              fontSize: "0.85rem",
            }}
          >
            Supprimer tout l'historique
          </Button>
        </Box>
      )}

      <Divider />

      {/* Conversations list */}
      <Box
        className="conversation-list"
        sx={{ flex: 1, overflowY: "auto", p: 1 }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <ChatIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? "Aucune conversation trouvée" : "Aucune conversation"}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {Object.entries(groupedConversations).map(([groupName, groupConvs]) =>
              groupConvs.length > 0 && (
                <Box key={groupName} sx={{ mb: 2 }}>
                  <Typography
                    component="div"
                    variant="caption"
                    sx={{
                      px: 2,
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                    }}
                  >
                    {groupName}
                  </Typography>

                  <List disablePadding sx={{ mt: 1 }}>
                    {groupConvs.map((conv) => {
                      const isActive = currentConversationId === conv.id;

                      return (
                        <ListItem
                          key={conv.id}
                          disablePadding
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => handleMenuOpen(e, conv.id)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          }
                          sx={{ mb: 0.5 }}
                        >
                          <ListItemButton
                            selected={isActive}
                            onClick={() => onSelectConversation(conv.id)}
                            sx={{
                              borderRadius: 2,
                              mx: 1,
                              "&.Mui-selected": {
                                bgcolor: "primary.50",
                                "&:hover": {
                                  bgcolor: "primary.100",
                                },
                              },
                            }}
                          >
                            <ListItemText
                              disableTypography
                              primary={
                                <Typography
                                  component="div"
                                  variant="body2"
                                  noWrap
                                  sx={{
                                    fontWeight: isActive ? 600 : 500,
                                    color: isActive
                                      ? "primary.main"
                                      : "text.primary",
                                  }}
                                >
                                  {conv.title || "Nouvelle conversation"}
                                </Typography>
                              }
                              secondary={
                                <Box
                                  component="div"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mt: 0.5,
                                  }}
                                >
                                  <ClockIcon
                                    sx={{
                                      fontSize: 12,
                                      mr: 0.5,
                                      color: "text.secondary",
                                    }}
                                  />
                                  <Typography
                                    component="div"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {formatTime(conv.created_at)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )
            )}
          </List>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 150,
              borderRadius: 2,
              boxShadow: theme.shadows[3],
            },
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            const conv = conversations.find((c) => c.id === activeMenuId);
            if (conv) {
              setNewTitle(conv.title);
              setShowRenameDialog(activeMenuId);
            }
            handleMenuClose();
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
          <Typography variant="body2">Renommer</Typography>
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteConfirm(activeMenuId);
            handleMenuClose();
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} />
          <Typography variant="body2" color="error.main">
            Supprimer
          </Typography>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(showDeleteConfirm)}
        onClose={() => setShowDeleteConfirm(null)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {showDeleteConfirm === "all"
              ? "Êtes-vous sûr de vouloir supprimer tout l'historique ? Cette action est irréversible."
              : "Êtes-vous sûr de vouloir supprimer cette conversation ?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteConfirm(null)}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Annuler
          </Button>
          <Button
            onClick={() =>
              showDeleteConfirm === "all"
                ? handleDeleteAll()
                : handleDelete(showDeleteConfirm)
            }
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={Boolean(showRenameDialog)}
        onClose={() => setShowRenameDialog(null)}
        PaperProps={{
          sx: { borderRadius: 3, p: 1, minWidth: 300 },
        }}
      >
        <DialogTitle>Renommer la conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            size="small"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowRenameDialog(null)}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Annuler
          </Button>
          <Button
            onClick={() => handleRename(showRenameDialog)}
            color="primary"
            variant="contained"
            disabled={!newTitle.trim()}
            sx={{ borderRadius: 2 }}
          >
            Renommer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationSidebar;
